// How the attributed-orders drill-down draws one component an order line was
// credited to.
//
// The API answers with a stable `channel` plus its own `label` and, where the
// entity still exists, an `entity` carrying the Postgres id and whatever extra
// columns a link needs. Which WORDS and which ROUTE go with a channel is the
// admin's business, not the API's — the At a Glance tab already calls a
// recommender a "product block", and two names for one thing two clicks apart is
// how a reader ends up looking for the wrong page.

// Per channel: the name this app uses, and how to address the entity behind it.
//
// `href` is called only with a non-null `entity`, so it never has to test for
// one. A channel with no `href` is one with nothing to link TO — search and the
// sales agent attribute a purchase without there being an entity to open — and
// renders as a plain label.
const CHANNELS = {
  campaign: {
    label: 'Campaign',
    // A workflow campaign is edited under /workflows and everything else under
    // /campaigns, and both detail pages read `mode` for their breadcrumbs — the
    // same derivation campaignHref does for the tab's own campaign tables.
    href: (base, entity) =>
      `${base}/${entity.type === 'workflow' ? 'workflows' : 'campaigns'}/${entity.id}/details` +
      (entity.mode ? `?mode=${entity.mode}` : ''),
  },
  email: { label: 'Email', href: (base, entity) => `${base}/emails/${entity.id}/details` },
  pushNotification: { label: 'Push notification', href: (base, entity) => `${base}/push/${entity.id}/details` },
  textMessage: { label: 'SMS', href: (base, entity) => `${base}/textMessages/${entity.id}/details` },
  viberMessage: { label: 'Viber', href: (base, entity) => `${base}/viber/${entity.id}/details` },
  whatsappMessage: { label: 'WhatsApp', href: (base, entity) => `${base}/whatsapp/${entity.id}/details` },
  webhook: { label: 'Webhook', href: (base, entity) => `${base}/webhooks/${entity.id}/details` },
  // No route of its own: an inbox message is authored as part of the push
  // notification that delivers it, so there is no page to send a reader to.
  inboxMessage: { label: 'Inbox message' },
  // Named the way the At a Glance tab names it, which is not what the API calls
  // it. A product block is addressed through its page, so `pageId` comes back
  // alongside the id for exactly this.
  recommender: {
    label: 'Product block',
    href: (base, entity) =>
      entity.pageId == null ? null : `${base}/pages/${entity.pageId}/recommenders/${entity.id}/details`,
  },
  banner: { label: 'Banner', href: (base, entity) => `${base}/banners/${entity.id}/details` },
  story: { label: 'Story', href: (base, entity) => `${base}/stories/${entity.id}/details` },
  facebookAd: { label: 'Facebook ad', href: (base, entity) => `${base}/facebook/ads/${entity.id}/details` },
  googleAd: { label: 'Google ad', href: (base, entity) => `${base}/google/ads/${entity.id}/details` },
  salesAgent: { label: 'Sales agent' },
  search: { label: 'Search as you type' },
};

/**
 * The hash-route base every link below hangs off. HashRouter, so an absolute
 * link is `#/accounts/...` (see helpers/atAGlance.js).
 * @param {object} args
 * @param {number|string} args.accountId
 * @param {number|string} args.domainId
 * @returns {string}
 */
const domainBase = ({ accountId, domainId }) => `#/accounts/${accountId}/domains/${domainId}`;

/**
 * Draw one attribution: what to call it, and where it points.
 *
 * Falls back to the API's own `label` for a channel this app has not been taught
 * — a new attribution column should name itself rather than render blank — and
 * to no link, since an unknown channel has no known route either.
 *
 * @param {object} args
 * @param {number|string} args.accountId
 * @param {number|string} args.domainId
 * @param {object} args.attribution - `{ channel, label, id, entity }` from the endpoint
 * @returns {{ label: string, name: string|null, href: string|null }}
 *   `label` names the KIND of component, `name` the instance (null once it has
 *   been deleted, or for a channel that never had one), `href` where to open it.
 */
export const describeAttribution = ({ accountId, domainId, attribution }) => {
  const channel = CHANNELS[attribution.channel];
  const label = channel?.label || attribution.label || attribution.channel;
  const entity = attribution.entity;

  return {
    label,
    name: entity?.name || null,
    href: entity && channel?.href ? channel.href(domainBase({ accountId, domainId }), entity) : null,
  };
};

/**
 * Hash href for the profile that placed an order. Addressed by the id the EVENT
 * carries, which is what the profile routes resolve — the same id and the same
 * `/events` landing the events list links to.
 * @param {object} args
 * @param {number|string} args.accountId
 * @param {number|string} args.domainId
 * @param {string} args.profileId
 * @returns {string|null} null when the order carries no profile at all
 */
export const profileHref = ({ accountId, domainId, profileId }) =>
  profileId ? `${domainBase({ accountId, domainId })}/profiles/${encodeURIComponent(profileId)}/events` : null;

/**
 * What to call the person behind an order. Falls back through the identifiers a
 * profile may be missing, and finally to the raw profile id — which is what the
 * link addresses anyway, so a nameless row is still actionable.
 * @param {object} purchase - a `purchases` entry
 * @returns {string}
 */
export const profileLabel = (purchase) => {
  const profile = purchase.profile;

  if (!profile) {
    return purchase.profileId;
  }

  const name = [profile.firstName, profile.lastName].filter(Boolean).join(' ');
  return name || profile.email || profile.phoneNumber || purchase.profileId;
};

/**
 * Hash href for the drill-down itself, carrying the range the tab was showing.
 * The dashboard keeps its range in component state rather than the URL, so
 * without these the details page would open on its own default and quietly
 * report a different period than the card that linked to it.
 * @param {object} args
 * @param {number|string} args.accountId
 * @param {number|string} args.domainId
 * @param {string} args.startDate - ISO date "YYYY-MM-DD"
 * @param {string} args.endDate - ISO date "YYYY-MM-DD"
 * @returns {string}
 */
export const attributedPurchasesHref = ({ accountId, domainId, startDate, endDate }) =>
  `${domainBase({ accountId, domainId })}/analytics/attributedPurchases?startDate=${encodeURIComponent(
    startDate
  )}&endDate=${encodeURIComponent(endDate)}`;

// How an order's refund status is drawn. Yellow for both refunded states, so a
// returned order is spotted while scanning rather than read for: a refund is the
// one thing on this page that undoes what the rest of it is claiming.
//
// The two are told apart by weight, not by hue — `filled` for an order that came
// back whole, `light` for one still partly earned — because a reader scanning the
// column is first asking "did any of this come back", and only then how much.
//
// `notRefunded` is a muted badge rather than a blank cell: it says the question
// was asked and answered. A null status says it could not be asked at all (no
// order id to match on, or a domain that maps no refund action), which is a
// different thing and must not read as clean.
const REFUND_STATUSES = {
  refunded: { label: 'Refunded', color: 'yellow', variant: 'filled' },
  partiallyRefunded: { label: 'Partially refunded', color: 'yellow', variant: 'light' },
  notRefunded: { label: 'No refund', color: 'gray', variant: 'outline' },
};

/**
 * Draw one order's refund status.
 *
 * @param {object} purchase - a `purchases` entry; reads `status`
 * @returns {{ label: string, color: string, variant: string }|null}
 *   null when the endpoint could not determine a status, which the caller must
 *   render as unknown rather than as "not refunded".
 */
export const describeRefundStatus = (purchase) => REFUND_STATUSES[purchase?.status] || null;
